import { useEffect, useState, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { supabase } from '@/lib/supabase'
import { getIgrejaIdByDomain } from '@/lib/igreja'
import { queryKeys } from '@/lib/react-query'
import { logger } from '@/lib/logger'
import { LoadingPage } from '@/components/ui/spinner'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { AlertCircle, CheckCircle } from 'lucide-react'

export function AuthCallbackPage() {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<'loading' | 'success' | 'error'>('loading')
  const [message, setMessage] = useState('')
  const hasProcessed = useRef(false)

  useEffect(() => {
    if (hasProcessed.current) {
      logger.info('Callback já processado, ignorando', { context: 'AUTH_CALLBACK' })
      return
    }

    hasProcessed.current = true

    const handleAuthCallback = async () => {
      try {
        // Marcar que estamos em um fluxo OAuth para evitar que o auth listener faça logout
        // (redundância - já deve estar setada pelo login mutation, mas garantir aqui também)
        if (localStorage.getItem('oauth_flow_active') !== 'true') {
          console.log('🔵 [CALLBACK] Flag oauth_flow_active não estava definida, definindo agora')
          localStorage.setItem('oauth_flow_active', 'true')
        } else {
          console.log('🔵 [CALLBACK] Flag oauth_flow_active já estava definida')
        }

        console.log('🔵 [CALLBACK] Iniciando processo de callback OAuth')
        console.log('🔵 [CALLBACK] URL atual:', window.location.href)
        console.log('🔵 [CALLBACK] Domínio:', window.location.host)
        console.log('🔵 [CALLBACK] Protocolo:', window.location.protocol)

        logger.info('Processando callback OAuth', { context: 'AUTH_CALLBACK' })

        if (!supabase) {
          throw new Error('Supabase client não disponível')
        }

        // 1. Obter sessão do Supabase
        console.log('🔵 [CALLBACK] Obtendo sessão do Supabase...')

        // Aguardar um pouco para o Supabase processar o token do hash
        console.log('🔵 [CALLBACK] Aguardando 1s para processar token...')
        await new Promise(resolve => setTimeout(resolve, 1000))

        console.log('🔵 [CALLBACK] Chamando getSession()...')
        const sessionPromise = supabase.auth.getSession()

        // Timeout de 10 segundos
        const timeoutPromise = new Promise<never>((_, reject) =>
          setTimeout(() => reject(new Error('Timeout ao obter sessão')), 10000)
        )

        const { data: sessionData, error: sessionError } = await Promise.race([
          sessionPromise,
          timeoutPromise
        ]).catch(error => {
          console.error('❌ [CALLBACK] Timeout ou erro:', error)
          throw error
        })

        console.log('🔵 [CALLBACK] Resultado da sessão:', {
          hasSession: !!sessionData?.session,
          hasUser: !!sessionData?.session?.user,
          error: sessionError
        })

        if (sessionError) {
          console.error('❌ [CALLBACK] Erro ao obter sessão:', sessionError)
          throw new Error(`Erro ao obter sessão: ${sessionError.message}`)
        }

        if (!sessionData?.session?.user) {
          console.error('❌ [CALLBACK] Sessão não encontrada')
          throw new Error('Sessão não encontrada. Por favor, tente fazer login novamente.')
        }

        const user = sessionData.session.user
        console.log('✅ [CALLBACK] Sessão encontrada:', {
          userId: user.id,
          email: user.email
        })
        logger.info('Sessão OAuth obtida', { context: 'AUTH_CALLBACK' })

        // 2. Detectar igreja pelo domínio
        console.log('🔵 [CALLBACK] Detectando igreja pelo domínio...')
        console.log('🔵 [CALLBACK] window.location.href:', window.location.href)
        console.log('🔵 [CALLBACK] window.location.host:', window.location.host)
        console.log('🔵 [CALLBACK] window.location.protocol:', window.location.protocol)
        console.log('🔵 [CALLBACK] window.location.origin:', window.location.origin)

        const currentHost = window.location.host
        let igrejaId = await getIgrejaIdByDomain(currentHost)

        // Fallback: Se não conseguiu detectar pelo domínio, tentar pegar do cache/localStorage
        if (!igrejaId) {
          console.warn('⚠️ [CALLBACK] Falha ao detectar igreja pelo domínio, tentando fallback...')

          // Tentar buscar novamente aguardando mais tempo
          await new Promise(resolve => setTimeout(resolve, 1000))
          igrejaId = await getIgrejaIdByDomain(currentHost)

          if (!igrejaId) {
            console.error('❌ [CALLBACK] Igreja não identificada após retry')
            throw new Error('Não foi possível identificar a igreja para este domínio. Verifique se o domínio está cadastrado na tabela de igrejas.')
          }

          console.log('✅ [CALLBACK] Igreja identificada no retry:', igrejaId)
        } else {
          console.log('✅ [CALLBACK] Igreja identificada:', igrejaId)
        }

        logger.info('Igreja identificada', { context: 'AUTH_CALLBACK', igrejaId })

        // 3. Verificar se usuário já existe
        console.log('🔵 [CALLBACK] Verificando se usuário já existe...')
        console.log('🔵 [CALLBACK] User ID:', user.id)
        console.log('🔵 [CALLBACK] User Email:', user.email)

        const { data: existingUser, error: checkError } = await supabase
          .from('usuarios')
          .select('id, email, auth_user_id, igreja_id')
          .or(`auth_user_id.eq.${user.id},email.eq.${user.email}`)
          .maybeSingle()

        if (checkError) {
          console.error('❌ [CALLBACK] Erro ao verificar usuário:', checkError)
          throw new Error(`Erro ao verificar usuário: ${checkError.message}`)
        }

        if (existingUser) {
          console.log('✅ [CALLBACK] Usuário existente encontrado:', existingUser.id)
          logger.info('Usuário existente encontrado', { context: 'AUTH_CALLBACK' })

          // Atualizar auth_user_id se necessário
          if (!existingUser.auth_user_id && existingUser.email === user.email) {
            console.log('🔵 [CALLBACK] Vinculando conta existente...')
            const { error: updateError } = await supabase
              .from('usuarios')
              .update({ auth_user_id: user.id })
              .eq('id', existingUser.id)

            if (updateError) {
              console.error('❌ [CALLBACK] Erro ao vincular conta:', updateError)
              throw new Error(`Erro ao vincular conta: ${updateError.message}`)
            }

            console.log('✅ [CALLBACK] Conta vinculada com sucesso')
            logger.info('Conta vinculada com sucesso', { context: 'AUTH_CALLBACK' })
          }
        } else {
          // 4. Criar novo usuário
          console.log('🔵 [CALLBACK] Criando novo usuário...')
          logger.info('Criando novo usuário', { context: 'AUTH_CALLBACK' })

          const newUserData = {
            auth_user_id: user.id,
            nome: user.user_metadata?.full_name || user.email?.split('@')[0] || 'Usuário',
            email: user.email || '',
            telefone: user.user_metadata?.phone || '',
            igreja_id: igrejaId,
            permissao: 'membro' as const,
            ativo: true,
            primeiro_acesso: false, // OAuth não precisa trocar senha
          }

          console.log('🔵 [CALLBACK] Dados do novo usuário:', {
            ...newUserData,
            auth_user_id: '***',
            email: user.email
          })

          const { error: insertError, data: insertedUser } = await supabase
            .from('usuarios')
            .insert(newUserData)
            .select()
            .single()

          // Retry uma vez se falhar por RLS
          if (insertError && insertError.message.includes('policy')) {
            console.warn('⚠️ [CALLBACK] Erro de RLS, tentando novamente em 1s...')
            logger.warn('Retry por erro de RLS', { context: 'AUTH_CALLBACK' })
            await new Promise(resolve => setTimeout(resolve, 1000))

            const { error: retryError, data: retryUser } = await supabase
              .from('usuarios')
              .insert(newUserData)
              .select()
              .single()

            if (retryError && !retryError.message.includes('duplicate')) {
              console.error('❌ [CALLBACK] Erro ao criar perfil (retry):', retryError)
              throw new Error(`Erro ao criar perfil: ${retryError.message}`)
            }

            if (retryUser) {
              console.log('✅ [CALLBACK] Usuário criado com sucesso (retry):', retryUser.id)
            }
          } else if (insertError && !insertError.message.includes('duplicate')) {
            console.error('❌ [CALLBACK] Erro ao criar perfil:', insertError)
            throw new Error(`Erro ao criar perfil: ${insertError.message}`)
          }

          if (insertedUser) {
            console.log('✅ [CALLBACK] Usuário criado com sucesso:', insertedUser.id)
          }

          logger.info('Usuário criado com sucesso', { context: 'AUTH_CALLBACK' })
        }

        // 4.5. Verificar se o perfil foi realmente criado antes de continuar
        console.log('🔵 [CALLBACK] Verificando se o perfil foi criado...')
        const { data: verifyProfile, error: verifyError } = await supabase
          .from('usuarios')
          .select('id, nome, igreja_id')
          .eq('auth_user_id', user.id)
          .maybeSingle()

        if (verifyError) {
          console.error('❌ [CALLBACK] Erro ao verificar criação do perfil:', verifyError)
          throw new Error('Erro ao verificar a criação do perfil')
        }

        if (!verifyProfile) {
          console.error('❌ [CALLBACK] Perfil não foi criado corretamente')
          throw new Error('O perfil não foi criado. Por favor, tente novamente.')
        }

        console.log('✅ [CALLBACK] Perfil verificado com sucesso:', {
          profileId: verifyProfile.id,
          userName: verifyProfile.nome,
          igrejaId: verifyProfile.igreja_id
        })

        // 5. Invalidar queries e redirecionar
        queryClient.invalidateQueries({ queryKey: queryKeys.auth.perfil(user.id) })
        queryClient.invalidateQueries({ queryKey: queryKeys.auth.session })

        // Limpar flag de OAuth flow
        localStorage.removeItem('oauth_flow_active')

        setStatus('success')
        setMessage('Autenticação realizada com sucesso!')

        setTimeout(() => {
          logger.info('Redirecionando para dashboard', { context: 'AUTH_CALLBACK' })
          navigate('/', { replace: true })
        }, 1500)

      } catch (error) {
        logger.error('Erro no callback', { context: 'AUTH_CALLBACK' })
        console.error('Erro no callback:', error)

        // Limpar flag de OAuth flow em caso de erro também
        localStorage.removeItem('oauth_flow_active')

        setStatus('error')
        setMessage(error instanceof Error ? error.message : 'Erro desconhecido')

        setTimeout(() => {
          navigate('/login', { replace: true })
        }, 4000)
      }
    }

    void handleAuthCallback()
  }, [navigate, queryClient])

  if (status === 'loading') {
    return <LoadingPage message="Finalizando autenticação..." variant="bounce" size="lg" />
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary/10 via-background to-secondary/10 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.5 }}
      >
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: 'spring', stiffness: 200 }}
              className={`mx-auto flex h-16 w-16 items-center justify-center rounded-full ${
                status === 'success'
                  ? 'bg-green-100 text-green-600'
                  : 'bg-red-100 text-red-600'
              }`}
            >
              {status === 'success' ? (
                <CheckCircle className="h-8 w-8" />
              ) : (
                <AlertCircle className="h-8 w-8" />
              )}
            </motion.div>
            <CardTitle className="text-xl">
              {status === 'success' ? 'Sucesso!' : 'Erro na Autenticação'}
            </CardTitle>
            <CardDescription>
              {message}
            </CardDescription>
          </CardHeader>
          <CardContent className="text-center">
            <p className="text-sm text-muted-foreground">
              {status === 'success'
                ? 'Você será redirecionado em alguns segundos...'
                : 'Redirecionando para a página de login...'
              }
            </p>
            {status === 'success' && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 1 }}
                className="mt-4"
              >
                <div className="flex items-center justify-center space-x-1">
                  <div className="h-2 w-2 bg-primary rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                  <div className="h-2 w-2 bg-primary rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                  <div className="h-2 w-2 bg-primary rounded-full animate-bounce"></div>
                </div>
              </motion.div>
            )}
          </CardContent>
        </Card>
      </motion.div>
    </div>
  )
}
